const IOS_DEVICE_UA = /iphone|ipad|ipod/
// cspell:disable-next-line
const MOBILE_WECHAT_UA = /android|harmonyos|openharmony|arkweb/

export type WechatVideoFullscreenMode = 'default' | 'system' | 'inline'
export type WechatFullscreenResult = 'system' | 'inline' | 'none'

const INLINE_FS_CLASS = 'vjs-wechat-inline-fs'
const INLINE_FS_STYLE_ID = 'byecode-wechat-video-fullscreen'

type ScrollLock = {
    el: HTMLElement
    overflowY: string
}

type FullscreenHost = {
    parent: Node | null
    next: ChildNode | null
}

export type InlineFullscreenSession = {
    isActive: () => boolean
    enter: (el: HTMLElement) => boolean
    exit: () => void
}

type AliFullscreenPlayer = {
    fullscreenService?: {
        requestFullScreen: () => void
        cancelFullScreen: () => void
        getIsFullScreen: () => boolean
    }
    on?: (eventName: string, callback: () => void) => void
}

export const readUserAgent = () => (typeof navigator === 'undefined' ? '' : navigator.userAgent)

/**
 * iPhone / iPad 走 WKWebView，系统全屏可用，这里不接管。
 * 安卓、鸿蒙、PC 小程序：全屏 video 元素。全屏外层 div 会闪退。
 * PC 用标准 requestFullscreen。失败再页内铺满。
 */
export const getWechatVideoFullscreenMode = (userAgent: string): WechatVideoFullscreenMode => {
    const ua = userAgent.toLowerCase()
    if (IOS_DEVICE_UA.test(ua)) {
        return 'default'
    }
    if (ua.includes('miniprogram')) {
        return 'system'
    }
    if (ua.includes('micromessenger') && MOBILE_WECHAT_UA.test(ua)) {
        return 'system'
    }
    return 'default'
}

export const prefersStandardVideoFullscreen = (userAgent: string) =>
    getWechatVideoFullscreenMode(userAgent) === 'system' && !MOBILE_WECHAT_UA.test(userAgent.toLowerCase())

export const shouldUseInlineVideoFullscreen = (userAgent: string) => getWechatVideoFullscreenMode(userAgent) === 'inline'

export const applySameLayerPlayAttributes = (video: HTMLElement) => {
    video.setAttribute('playsinline', 'true')
    video.setAttribute('webkit-playsinline', 'true')
    video.setAttribute('x5-playsinline', 'true')
    video.setAttribute('x5-video-player-type', 'h5')
    video.setAttribute('x5-video-player-fullscreen', 'false')
}

export const applyInlineVideoAttributes = (video: HTMLElement) => {
    applySameLayerPlayAttributes(video)
    // cspell:disable-next-line
    video.setAttribute('controlsList', 'nofullscreen')
}

type SystemVideo = HTMLVideoElement & {
    webkitEnterFullScreen?: () => void
    webkitEnterFullscreen?: () => void
    webkitExitFullScreen?: () => void
    webkitExitFullscreen?: () => void
}

const NATIVE_CONTROLS_FLAG = 'data-byecode-fs-controls'

const restoreNativeControls = (video: HTMLVideoElement) => {
    if (video.getAttribute(NATIVE_CONTROLS_FLAG) !== '1') {
        return
    }
    video.controls = false
    video.removeAttribute(NATIVE_CONTROLS_FLAG)
}

const requestStandardFullscreen = (video: HTMLVideoElement, onFail?: () => void) => {
    if (typeof video.requestFullscreen !== 'function') {
        return false
    }
    if (!video.controls) {
        video.controls = true
        video.setAttribute(NATIVE_CONTROLS_FLAG, '1')
    }
    try {
        const pending = video.requestFullscreen()
        void pending?.catch?.(() => {
            restoreNativeControls(video)
            onFail?.()
        })
        return true
    } catch {
        restoreNativeControls(video)
        return false
    }
}

export const enterSystemVideoFullscreen = (video: HTMLVideoElement, options?: { preferStandard?: boolean; onStandardFail?: () => void }) => {
    video.setAttribute('playsinline', 'true')
    video.setAttribute('webkit-playsinline', 'true')
    video.setAttribute('x5-playsinline', 'true')
    video.setAttribute('x5-video-player-type', 'h5')
    video.setAttribute('x5-video-player-fullscreen', 'true')
    video.setAttribute('x5-video-orientation', 'landscape|portrait')
    try {
        const orientation = typeof screen === 'undefined' ? undefined : screen.orientation
        void orientation?.unlock?.()?.catch(() => undefined)
    } catch {
        // 部分 WebView 不允许页面改方向，系统播放器自己处理
    }
    if (options?.preferStandard) {
        if (!requestStandardFullscreen(video, options.onStandardFail)) {
            options.onStandardFail?.()
        }
        return
    }
    const media = video as SystemVideo
    if (typeof media.webkitEnterFullScreen === 'function') {
        media.webkitEnterFullScreen()
        return
    }
    if (typeof media.webkitEnterFullscreen === 'function') {
        media.webkitEnterFullscreen()
        return
    }
    requestStandardFullscreen(video)
}

export const exitSystemVideoFullscreen = (video: HTMLVideoElement) => {
    const media = video as SystemVideo
    try {
        if (typeof document !== 'undefined' && document.fullscreenElement && typeof document.exitFullscreen === 'function') {
            void document.exitFullscreen()
        } else if (typeof media.webkitExitFullScreen === 'function') {
            media.webkitExitFullScreen()
        } else if (typeof media.webkitExitFullscreen === 'function') {
            media.webkitExitFullscreen()
        }
    } catch {
        // 已经不在系统全屏里
    }
    restoreNativeControls(video)
}

export const requestWechatVideoFullscreen = (options: {
    userAgent: string
    video: HTMLVideoElement | null
    playerEl: HTMLElement | null
    inline: InlineFullscreenSession
    onInline?: () => void
}): WechatFullscreenResult => {
    const mode = getWechatVideoFullscreenMode(options.userAgent)
    const enterInline = () => {
        if (options.playerEl && options.inline.enter(options.playerEl)) {
            options.onInline?.()
            return true
        }
        return false
    }
    if (mode === 'system' && options.video) {
        enterSystemVideoFullscreen(options.video, {
            preferStandard: prefersStandardVideoFullscreen(options.userAgent),
            onStandardFail: enterInline
        })
        return 'system'
    }
    if (mode === 'inline' && enterInline()) {
        return 'inline'
    }
    return 'none'
}

const ensureInlineFullscreenStyle = () => {
    if (typeof document === 'undefined' || document.getElementById(INLINE_FS_STYLE_ID)) {
        return
    }
    const style = document.createElement('style')
    style.id = INLINE_FS_STYLE_ID
    style.textContent = `
.${INLINE_FS_CLASS},
.prism-player.${INLINE_FS_CLASS} {
    position: fixed !important;
    inset: 0 !important;
    left: 0 !important;
    top: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 100% !important;
    max-height: none !important;
    margin: 0 !important;
    clip-path: none !important;
    z-index: 2147483000 !important;
    background-color: #000 !important;
}
`
    document.head.appendChild(style)
}

const lockScrollableAncestors = (start: Element | null): ScrollLock[] => {
    const locks: ScrollLock[] = []
    let node = start?.parentElement ?? null
    while (node) {
        const overflowY = window.getComputedStyle(node).overflowY
        if (overflowY === 'auto' || overflowY === 'scroll') {
            locks.push({ el: node, overflowY: node.style.overflowY })
            node.style.overflowY = 'hidden'
        }
        node = node.parentElement
    }
    return locks
}

const restoreScrollLocks = (locks: ScrollLock[]) => {
    locks.forEach(({ el, overflowY }) => {
        el.style.overflowY = overflowY
    })
}

const assignNativeFullscreen = (el: HTMLElement, key: string, value: () => void | Promise<void>) => {
    try {
        Object.defineProperty(el, key, {
            configurable: true,
            value
        })
    } catch {
        // 内核把方法锁死时，播放器入口的替换仍然挡住 video.js / Aliplayer 的调用。
    }
}

export const blockNativeFullscreen = (el: HTMLElement, handlers: { enter: () => void; exit: () => void }) => {
    const enter = () => {
        handlers.enter()
        return Promise.resolve()
    }
    const exit = () => {
        handlers.exit()
        return Promise.resolve()
    }
    assignNativeFullscreen(el, 'requestFullscreen', enter)
    assignNativeFullscreen(el, 'webkitRequestFullscreen', enter)
    assignNativeFullscreen(el, 'webkitEnterFullscreen', enter)
    assignNativeFullscreen(el, 'webkitEnterFullScreen', enter)
    assignNativeFullscreen(el, 'exitFullscreen', exit)
    assignNativeFullscreen(el, 'webkitExitFullscreen', exit)
    assignNativeFullscreen(el, 'webkitExitFullScreen', exit)
}

export const createInlineFullscreenSession = (): InlineFullscreenSession => {
    const host: FullscreenHost = { parent: null, next: null }
    let scrollLocks: ScrollLock[] = []
    let bodyOverflow = ''
    let active = false
    let current: HTMLElement | null = null

    const exit = () => {
        if (!active) {
            return
        }
        if (current?.classList.contains(INLINE_FS_CLASS)) {
            current.classList.remove(INLINE_FS_CLASS)
            const { parent, next } = host
            if (parent?.isConnected) {
                if (next && next.parentNode === parent) {
                    parent.insertBefore(current, next)
                } else {
                    parent.appendChild(current)
                }
            }
        }
        current = null
        host.parent = null
        host.next = null
        restoreScrollLocks(scrollLocks)
        scrollLocks = []
        document.body.style.overflow = bodyOverflow
        active = false
    }

    return {
        isActive: () => active,
        enter: el => {
            if (active) {
                return true
            }
            if (!el.parentNode) {
                return false
            }
            ensureInlineFullscreenStyle()
            scrollLocks = lockScrollableAncestors(el)
            bodyOverflow = document.body.style.overflow
            document.body.style.overflow = 'hidden'
            host.parent = el.parentNode
            host.next = el.nextSibling
            el.classList.add(INLINE_FS_CLASS)
            current = el
            document.body.appendChild(el)
            active = true
            return true
        },
        exit
    }
}

const tagInlineVideos = (root: HTMLElement, desktopInline: boolean) => {
    const apply = desktopInline ? applyInlineVideoAttributes : applySameLayerPlayAttributes
    if (root instanceof HTMLVideoElement) {
        apply(root)
    }
    root.querySelectorAll('video').forEach(node => {
        if (node instanceof HTMLElement) {
            apply(node)
        }
    })
}

const videoIsSystemFullscreen = (video: HTMLVideoElement) => {
    const media = video as SystemVideo & { webkitDisplayingFullscreen?: boolean }
    if (media.webkitDisplayingFullscreen) {
        return true
    }
    return typeof document !== 'undefined' && document.fullscreenElement === video
}

export const installAliPlayerInlineFullscreen = (root: HTMLElement, player: AliFullscreenPlayer) => {
    const userAgent = readUserAgent()
    const desktopInline = shouldUseInlineVideoFullscreen(userAgent)
    const session = createInlineFullscreenSession()
    let stopped = false

    const syncButton = (on: boolean) => {
        root.querySelectorAll('.prism-fullscreen-btn').forEach(button => {
            button.classList.toggle('fullscreen', on)
        })
    }

    const showFullscreen = (result: WechatFullscreenResult) => {
        if (result === 'inline' || result === 'system') {
            syncButton(true)
        }
    }

    const readVideo = () => {
        const video = root.querySelector('video')
        return video instanceof HTMLVideoElement ? video : null
    }

    const enter = () => {
        if (stopped) {
            return
        }
        tagInlineVideos(root, desktopInline)
        const media = readVideo()
        const result = requestWechatVideoFullscreen({
            userAgent,
            video: media,
            playerEl: root,
            inline: session,
            onInline: () => syncButton(true)
        })
        showFullscreen(result)
    }

    const exit = () => {
        const media = readVideo()
        if (media) {
            exitSystemVideoFullscreen(media)
        }
        session.exit()
        if (root.isConnected) {
            syncButton(false)
        }
    }

    const installService = () => {
        const service = player.fullscreenService
        if (!service) {
            return
        }
        service.requestFullScreen = enter
        service.cancelFullScreen = exit
        service.getIsFullScreen = () => {
            if (session.isActive()) {
                return true
            }
            const media = readVideo()
            return media ? videoIsSystemFullscreen(media) : false
        }
    }

    const patchTree = () => {
        const nodes: HTMLElement[] = [root]
        if (desktopInline) {
            root.querySelectorAll('video').forEach(node => {
                if (node instanceof HTMLElement) {
                    nodes.push(node)
                }
            })
        }
        nodes.forEach(node => {
            blockNativeFullscreen(node, {
                enter: () => {
                    if (!session.isActive()) {
                        enter()
                    }
                },
                exit
            })
        })
    }

    installService()
    tagInlineVideos(root, desktopInline)
    patchTree()
    player.on?.('ready', () => {
        if (stopped) {
            return
        }
        installService()
        tagInlineVideos(root, desktopInline)
        patchTree()
    })

    const observer =
        typeof MutationObserver === 'undefined'
            ? null
            : new MutationObserver(() => {
                  if (stopped) {
                      return
                  }
                  tagInlineVideos(root, desktopInline)
                  patchTree()
              })
    observer?.observe(root, { childList: true, subtree: true })

    return () => {
        stopped = true
        observer?.disconnect()
        exit()
    }
}
